<script setup lang="ts">
/**
 * Pilule « Propulsé par Donoor.org » des tunnels de paiement hébergés
 * (scan QR, cagnotte, lien de paiement, page de don personnalisée).
 *
 * Décision Karim 2026-09-11 : ces tunnels affichaient « Propulsé par stripe »
 * (le PSP qui encaisse). C'est Donoor qui propulse le tunnel — la marque
 * affichée est Donoor, et le clic ouvre donoor.org dans un nouvel onglet.
 *
 * Même dessin ET MÊME LIBELLÉ que la pilule des widgets embarqués
 * (`EmbedPoweredBy`, `DonateEmbedApp`) : fond bleu nuit #040a32, D blanc au
 * cœur bleu nuit, « Donoor.org » en jaune marque #ffcf01. SVG inline : pas de
 * requête réseau supplémentaire sur une page de paiement.
 *
 * Le libellé est « Donoor.org » et PAS « Donoor » (Karim 2026-09-11) : c'est
 * la forme des quatre autres pilules du site, et c'est elle qui porte le
 * maillage SEO du backlink. Deux formes en circulation diluaient le signal.
 *
 * `showLegalLinks` ajoute les conditions + confidentialité Donoor sous la
 * pilule — utile là où le tunnel n'affiche pas déjà ses propres mentions.
 */
withDefaults(defineProps<{ showLegalLinks?: boolean }>(), {
  showLegalLinks: false,
});

const DONOOR_SITE_URL = "https://donoor.org?utm_source=tunnel&utm_medium=powered-by";

const { t } = useI18n();
const localePath = useLocalePath();
</script>

<template>
  <div class="flex flex-col items-center space-y-3">
    <a
      :href="DONOOR_SITE_URL"
      target="_blank"
      rel="noopener noreferrer"
      :title="t('don.poweredBy.titre')"
      class="donate-powered-by inline-flex items-center gap-1.5 h-8 pl-2 pr-3 rounded-full bg-[#040a32] text-[12px] leading-none no-underline transition-[transform,box-shadow] duration-150 hover:-translate-y-px"
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 64 64"
        fill="none"
        aria-hidden="true"
        class="shrink-0"
      >
        <path
          d="M12 10 C12 7.79 13.79 6 16 6 L36 6 C50 6 58 16 58 32 C58 48 50 58 36 58 L16 58 C13.79 58 12 56.21 12 54 Z"
          fill="#ffffff"
        />
        <path
          d="M28 29 C26 27 23 27 23 30 C23 33 28 37 28 37 C28 37 33 33 33 30 C33 27 30 27 28 29 Z"
          fill="#040a32"
        />
      </svg>
      <i18n-t keypath="don.poweredBy.propulsePar" tag="span" class="whitespace-nowrap text-[#ffcf01]">
        <template #marque><b class="font-bold">Donoor.org</b></template>
      </i18n-t>
    </a>
    <div v-if="showLegalLinks" class="flex justify-center space-x-4 text-sm">
      <a
        :href="localePath({ path: '/terms' })"
        target="_blank"
        rel="noopener noreferrer"
        class="text-[#0c3097] hover:opacity-80"
      >
        {{ t('don.poweredBy.conditionsUtilisation') }}
      </a>
      <a
        :href="localePath({ path: '/privacy' })"
        target="_blank"
        rel="noopener noreferrer"
        class="text-[#0c3097] hover:opacity-80"
      >
        {{ t('don.poweredBy.confidentialite') }}
      </a>
    </div>
  </div>
</template>

<style scoped>
.donate-powered-by {
  box-shadow: 0 1px 6px rgba(4, 10, 50, 0.22);
}
.donate-powered-by:hover {
  box-shadow: 0 3px 11px rgba(4, 10, 50, 0.36);
}
</style>
